import React, { useState, useMemo } from 'react';

const APARTMENTS = {
  F2: {
    label: 'F2',
    icon: '👫',
    surface: '43 – 60 m²',
    rooms: '1 chambre + salon',
    units: [
      { code: 'F2-A', surface: 43, price: 40000000 },
      { code: 'F2-B', surface: 60.47, price: 54423000 },
    ],
  },
  F3: {
    label: 'F3',
    icon: '👨‍👩‍👧',
    surface: '88 m²',
    rooms: '2 chambres + salon',
    units: [
      { code: 'F3-B', surface: 88, price: 78300000 },
    ],
  },
  F4: {
    label: 'F4',
    icon: '👨‍👩‍👧‍👦',
    surface: '135 – 141 m²',
    rooms: '3 chambres + salon',
    units: [
      { code: 'F4-C', surface: 135, price: 121500000 },
      { code: 'F4-A', surface: 138, price: 124200000 },
      { code: 'F4-B', surface: 141, price: 126900000 },
    ],
  },
};

const DURATIONS = [3, 5, 8];
const RATE_ANNUAL = 0.07;
const MIN_APPORT_RATE = 0.50;

function formatFCFA(n) {
  return n.toLocaleString('fr-FR') + ' FCFA';
}

function monthlyPayment(principal, years) {
  const r = RATE_ANNUAL / 12;
  const n = years * 12;
  return (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

export default function RyadSimulation() {
  const [type, setType] = useState('F2');
  const [unitIndex, setUnitIndex] = useState(0);
  const [mode, setMode] = useState('comptant'); // 'comptant' | 'lv'
  const [duration, setDuration] = useState(5);
  const [apportInput, setApportInput] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [sent, setSent] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null); // null | 'sending' | 'success' | 'error'
  const [emailError, setEmailError] = useState('');
  const [formErrors, setFormErrors] = useState({});

  const apt = APARTMENTS[type];
  const unit = apt.units[unitIndex] ?? apt.units[0];
  const totalPrice = unit.price;

  const minApport = Math.ceil(totalPrice * MIN_APPORT_RATE);

  const apportValue = useMemo(() => {
    const v = parseInt(apportInput.replace(/\s/g, ''), 10);
    return isNaN(v) ? minApport : v;
  }, [apportInput, minApport]);

  const apportRate = apportValue / totalPrice;
  const apportValid = apportValue >= minApport;
  const principal = totalPrice - apportValue;

  const monthly = useMemo(() => {
    if (!apportValid || principal <= 0) return null;
    return monthlyPayment(principal, duration);
  }, [principal, duration, apportValid]);

  const totalCost = monthly ? monthly * duration * 12 + apportValue : null;
  const totalInterest = totalCost ? totalCost - totalPrice : null;

  const lvAllowed = type !== 'F2';

  const handleTypeChange = (t) => {
    setType(t);
    setUnitIndex(0);
    setApportInput('');
    if (t === 'F2') setMode('comptant');
  };

  const handleUnitChange = (i) => {
    setUnitIndex(i);
    setApportInput('');
  };

  const handleApportChange = (e) => {
    const raw = e.target.value.replace(/\s/g, '');
    if (/^\d*$/.test(raw)) setApportInput(raw);
  };

  const buildSimulationText = () => {
    const SCHEDULE = [
      { step: 'Réservation',    date: '—',         percent: 5  },
      { step: 'Démarrage',      date: 'Mai 2026',  percent: 30 },
      { step: 'Étage 2',        date: 'Sep 2026',  percent: 30 },
      { step: 'Étage 5',        date: 'Janv 2027', percent: 20 },
      { step: 'Finition',       date: 'Mai 2027',  percent: 10 },
      { step: 'Remise des Clés',date: 'Sept 2027', percent: 5  },
    ];
    const name = clientName.trim() || 'Client';
    if (mode === 'comptant') {
      const lines = SCHEDULE.map(s =>
        `  • ${s.step} (${s.date}) — ${s.percent}% → ${formatFCFA(Math.round(totalPrice * s.percent / 100))}`
      ).join('\n');
      return `Bonjour, je suis ${name}.\n\nVoici ma simulation d'achat pour la Résidence RYAD :\n\n🏠 Appartement : ${type} · ${unit.code} · ${unit.surface} m²\n💵 Mode : Achat Comptant\n💰 Prix total : ${formatFCFA(totalPrice)}\n\nÉchelonnement :\n${lines}\n\nMon numéro : ${clientPhone || 'à préciser'}\nMerci de me rappeler pour discuter de mon projet.`;
    } else {
      return `Bonjour, je suis ${name}.\n\nVoici ma simulation d'achat pour la Résidence RYAD :\n\n🏠 Appartement : ${type} · ${unit.code} · ${unit.surface} m²\n🏦 Mode : Location-Vente sur ${duration} ans\n💰 Prix total : ${formatFCFA(totalPrice)}\n💳 Apport : ${formatFCFA(apportValue)} (${(apportRate * 100).toFixed(1)}%)\n📊 Montant financé : ${formatFCFA(principal)}\n📅 Mensualité : ${monthly ? formatFCFA(Math.round(monthly)) : 'N/A'}\n\nMon numéro : ${clientPhone || 'à préciser'}\nMerci de me rappeler pour discuter de mon projet.`;
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!clientName.trim()) errors.name = 'Veuillez saisir votre nom';
    const digits = clientPhone.replace(/\D/g, '');
    if (!clientPhone.trim())   errors.phone = 'Veuillez saisir votre numéro de téléphone';
    else if (digits.length < 8) errors.phone = 'Numéro invalide (minimum 8 chiffres)';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSendWhatsApp = () => {
    if (!validateForm()) return;
    const msg = encodeURIComponent(buildSimulationText());
    window.open(`https://wa.me/221787172828?text=${msg}`, '_blank');
    setSent(true);
  };

  const handleSendEmail = async () => {
    if (!validateForm()) return;
    setEmailStatus('sending');
    setEmailError('');
    try {
      const res = await fetch('http://localhost:8001/send-simulation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name:  clientName.trim(),
          phone: clientPhone.trim(),
          body:  buildSimulationText(),
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setEmailStatus('success');
        setSent(true);
      } else {
        setEmailStatus('error');
        setEmailError(data.error || 'Erreur lors de l\'envoi');
      }
    } catch (err) {
      setEmailStatus('error');
      setEmailError('Impossible de joindre le serveur d\'envoi. Vérifiez que email_api.py est démarré.');
    }
  };

  const canSend = mode === 'comptant' || (mode === 'lv' && apportValid && monthly);

  return (
    <section id="simulation" className="py-20 bg-white relative overflow-hidden">
      {/* Background */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl" />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold mb-5 tracking-wide uppercase">
            🧮 Simulation d'Achat
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            Estimez votre <span className="text-amber-600">financement</span>
          </h2>
          <p className="text-gray-500 max-w-xl mx-auto text-base">
            Simulez votre achat comptant ou en location-vente pour chaque type d'appartement.
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">

          {/* ── Step 1 : Type ── */}
          <div className="px-6 pt-8 pb-6 border-b border-gray-100">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">
              1 · Choisissez le type d'appartement
            </p>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(APARTMENTS).map(([key, data]) => (
                <button
                  key={key}
                  onClick={() => handleTypeChange(key)}
                  className={`rounded-xl p-4 border-2 transition-all duration-200 text-center ${
                    type === key
                      ? 'border-amber-500 bg-amber-50 shadow-md'
                      : 'border-gray-200 hover:border-amber-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="text-3xl mb-1">{data.icon}</div>
                  <div className="font-bold text-gray-800 text-lg">{key}</div>
                  <div className="text-xs text-gray-500 mt-1">{data.surface}</div>
                  <div className="text-xs text-gray-400">{data.rooms}</div>
                </button>
              ))}
            </div>
          </div>

          {/* ── Step 2 : Unité ── */}
          {apt.units.length > 1 && (
            <div className="px-6 py-5 border-b border-gray-100 bg-gray-50">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                2 · Sélectionnez l'appartement
              </p>
              <div className="flex flex-wrap gap-2">
                {apt.units.map((u, i) => (
                  <button
                    key={u.code}
                    onClick={() => handleUnitChange(i)}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-all duration-200 ${
                      unitIndex === i
                        ? 'bg-amber-500 text-white border-amber-500 shadow'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-amber-400'
                    }`}
                  >
                    {u.code} — {u.surface} m²
                    <span className="ml-2 opacity-80">{formatFCFA(u.price)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Prix affiché */}
          <div className="px-6 py-4 bg-gradient-to-r from-gray-800 to-gray-900 flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-400 uppercase tracking-wide">Appartement sélectionné</div>
              <div className="text-white font-bold text-lg mt-0.5">
                {type} · {unit.code} · {unit.surface} m²
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-400 uppercase tracking-wide">Prix de vente</div>
              <div className="text-amber-400 font-bold text-xl mt-0.5">{formatFCFA(totalPrice)}</div>
            </div>
          </div>

          {/* ── Step 3 : Mode de paiement ── */}
          <div className="px-6 py-6 border-b border-gray-100">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">
              {apt.units.length > 1 ? '3' : '2'} · Mode de financement
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setMode('comptant')}
                className={`rounded-xl p-5 border-2 transition-all duration-200 text-left ${
                  mode === 'comptant'
                    ? 'border-green-500 bg-green-50 shadow-md'
                    : 'border-gray-200 hover:border-green-300'
                }`}
              >
                <div className="text-2xl mb-2">💵</div>
                <div className="font-bold text-gray-800">Achat Comptant</div>
                <div className="text-xs text-gray-500 mt-1">Paiement intégral du prix de vente</div>
              </button>
              {lvAllowed ? (
                <button
                  onClick={() => setMode('lv')}
                  className={`rounded-xl p-5 border-2 transition-all duration-200 text-left ${
                    mode === 'lv'
                      ? 'border-amber-500 bg-amber-50 shadow-md'
                      : 'border-gray-200 hover:border-amber-300'
                  }`}
                >
                  <div className="text-2xl mb-2">🏦</div>
                  <div className="font-bold text-gray-800">Location-Vente</div>
                  <div className="text-xs text-gray-500 mt-1">Apport minimum 50%</div>
                </button>
              ) : (
                <div className="rounded-xl p-5 border-2 border-gray-100 bg-gray-50 text-left opacity-50 cursor-not-allowed">
                  <div className="text-2xl mb-2">🏦</div>
                  <div className="font-bold text-gray-400">Location-Vente</div>
                  <div className="text-xs text-gray-400 mt-1">Non disponible pour le F2</div>
                </div>
              )}
            </div>
          </div>

          {/* ── Location-Vente options ── */}
          {mode === 'lv' && (
            <div className="px-6 py-6 border-b border-gray-100 bg-amber-50/40">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Durée */}
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3">
                    Durée de remboursement
                  </p>
                  <div className="flex gap-2">
                    {DURATIONS.map((d) => (
                      <button
                        key={d}
                        onClick={() => setDuration(d)}
                        className={`flex-1 py-3 rounded-xl font-bold text-sm border-2 transition-all duration-200 ${
                          duration === d
                            ? 'bg-amber-500 text-white border-amber-500 shadow'
                            : 'bg-white text-gray-700 border-gray-200 hover:border-amber-400'
                        }`}
                      >
                        {d} ans
                      </button>
                    ))}
                  </div>
                </div>

                {/* Apport */}
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3">
                    Apport personnel (min 50%)
                  </p>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={apportInput}
                    onChange={handleApportChange}
                    placeholder={minApport.toLocaleString('fr-FR')}
                    className={`w-full px-4 py-3 rounded-xl border-2 text-gray-800 font-semibold focus:outline-none transition-colors ${
                      apportInput && !apportValid
                        ? 'border-red-400 bg-red-50'
                        : 'border-gray-200 focus:border-amber-500 bg-white'
                    }`}
                  />
                  <div className={`mt-1 text-xs ${apportInput && !apportValid ? 'text-red-500' : 'text-gray-400'}`}>
                    {apportInput && !apportValid
                      ? `Minimum requis : ${formatFCFA(minApport)}`
                      : `Minimum 50% = ${formatFCFA(minApport)}`}
                  </div>
                  {apportInput && apportValid && (
                    <div className="mt-1 text-xs text-green-600 font-semibold">
                      Apport : {(apportRate * 100).toFixed(1)}% du prix total ✓
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── Résultats ── */}
          <div className="px-6 py-8">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-5">
              Résultat de la simulation
            </p>

            {mode === 'comptant' && (
              <div className="space-y-5">
                {/* Prix total */}
                <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4">
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Prix total</div>
                    <div className="text-2xl font-bold text-amber-700 mt-0.5">{formatFCFA(totalPrice)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Surface</div>
                    <div className="text-lg font-bold text-gray-800 mt-0.5">{unit.surface} m²</div>
                  </div>
                </div>

                {/* Tableau d'échelonnement */}
                <div className="rounded-2xl overflow-hidden shadow-md border border-slate-200">
                  {/* En-tête */}
                  <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-5 py-3">
                    <div className="grid grid-cols-4 gap-2 text-xs font-bold text-slate-300 uppercase tracking-widest">
                      <div>Étape</div>
                      <div>Échéance</div>
                      <div className="text-center">Quote-part</div>
                      <div className="text-right">Montant</div>
                    </div>
                  </div>

                  {/* Lignes */}
                  <div className="divide-y divide-slate-100">
                    {[
                      { step: 'Réservation',     date: '—',          percent: 5  },
                      { step: 'Démarrage',        date: 'Mai 2026',   percent: 30 },
                      { step: 'Étage 2',          date: 'Sep 2026',   percent: 30 },
                      { step: 'Étage 5',          date: 'Janv 2027',  percent: 20 },
                      { step: 'Finition',         date: 'Mai 2027',   percent: 10 },
                      { step: 'Remise des Clés',  date: 'Sept 2027',  percent: 5  },
                    ].map((item, i) => (
                      <div
                        key={i}
                        className={`grid grid-cols-4 gap-2 px-5 py-3.5 items-center text-sm transition-colors duration-150 ${
                          i % 2 === 0 ? 'bg-white' : 'bg-slate-50'
                        } hover:bg-amber-50/60`}
                      >
                        {/* Étape */}
                        <div className="font-semibold text-slate-800 text-xs leading-tight">{item.step}</div>
                        {/* Date */}
                        <div className="text-slate-400 text-xs font-medium">{item.date}</div>
                        {/* % badge */}
                        <div className="text-center">
                          <span className={`inline-flex items-center justify-center w-12 py-1 rounded-full text-xs font-bold border ${
                            item.percent >= 30
                              ? 'bg-amber-500 text-white border-amber-500'
                              : item.percent >= 20
                              ? 'bg-slate-700 text-white border-slate-700'
                              : 'bg-white text-slate-600 border-slate-300'
                          }`}>
                            {item.percent}%
                          </span>
                        </div>
                        {/* Montant */}
                        <div className="text-right">
                          <span className="font-bold text-slate-800 text-xs">
                            {formatFCFA(Math.round(totalPrice * item.percent / 100))}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Ligne Total */}
                  <div className="bg-gradient-to-r from-amber-500 to-amber-600 px-5 py-4">
                    <div className="grid grid-cols-4 gap-2 items-center">
                      <div className="col-span-2 flex items-center gap-2">
                        <span className="text-white font-bold text-sm uppercase tracking-wide">Total</span>
                      </div>
                      <div className="text-center">
                        <span className="inline-flex items-center justify-center w-12 py-1 rounded-full text-xs font-bold bg-white text-amber-600 border border-amber-200">
                          100%
                        </span>
                      </div>
                      <div className="text-right font-bold text-white text-sm">
                        {formatFCFA(totalPrice)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {mode === 'lv' && (!apportInput || !apportValid) && (
              <div className="text-center py-8 text-gray-400">
                <div className="text-4xl mb-3">🏦</div>
                <p className="text-base">
                  Renseignez votre apport (min{' '}
                  <span className="font-semibold text-amber-600">{formatFCFA(minApport)}</span>) pour voir la simulation.
                </p>
              </div>
            )}

            {mode === 'lv' && apportValid && monthly && (
              <div className="grid sm:grid-cols-2 gap-4">
                <ResultCard
                  icon="🏦"
                  label="Apport initial"
                  value={formatFCFA(apportValue)}
                  sub={`${(apportRate * 100).toFixed(1)}% du prix`}
                  highlight
                />
                <ResultCard
                  icon="💳"
                  label="Mensualité"
                  value={formatFCFA(Math.round(monthly))}
                  sub={`Sur ${duration * 12} mois (${duration} ans)`}
                  highlight
                />
                <ResultCard
                  icon="💰"
                  label="Montant financé"
                  value={formatFCFA(principal)}
                  sub="Prix - Apport"
                />
                <ResultCard
                  icon="📈"
                  label="Intérêts totaux"
                  value={formatFCFA(Math.round(totalInterest))}
                  sub={`Total payé : ${formatFCFA(Math.round(totalCost))}`}
                />
                <ResultCard
                  icon="🗓️"
                  label="Durée"
                  value={`${duration} ans`}
                  sub={`${duration * 12} mensualités`}
                />

                {/* Récapitulatif durée sélectionnée */}
                <div className="sm:col-span-2 rounded-2xl overflow-hidden border border-slate-200 shadow-sm mt-2">
                  <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-5 py-3 flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-300 uppercase tracking-widest">Récapitulatif — {duration} ans</span>
                    <span className="inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-bold bg-amber-500 text-white">{duration * 12} mensualités</span>
                  </div>
                  <div className="grid grid-cols-3 divide-x divide-slate-100 bg-white">
                    <div className="p-4 text-center">
                      <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">Mensualité</div>
                      <div className="font-bold text-amber-600 text-base">{formatFCFA(Math.round(monthly))}</div>
                    </div>
                    <div className="p-4 text-center">
                      <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">Total remboursé</div>
                      <div className="font-bold text-slate-800 text-base">{formatFCFA(Math.round(totalCost))}</div>
                    </div>
                    <div className="p-4 text-center">
                      <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">Coût du crédit</div>
                      <div className="font-bold text-red-500 text-base">+{formatFCFA(Math.round(totalInterest))}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Envoyer ma simulation ── */}
          {canSend && (
            <div className="px-6 pb-8">
              <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl overflow-hidden">
                {/* Header */}
                <div className="px-6 pt-6 pb-4 border-b border-white/10">
                  <p className="text-white font-bold text-lg">📤 Envoyer ma simulation</p>
                  <p className="text-slate-400 text-sm mt-1">
                    Recevez un récapitulatif complet et faites-vous rappeler par notre équipe.
                  </p>
                </div>

                <div className="px-6 py-5 space-y-4">
                  {/* Nom & Téléphone */}
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-400 font-semibold uppercase tracking-wide mb-1.5 block">
                        Votre nom <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={clientName}
                        onChange={e => { setClientName(e.target.value); setSent(false); setEmailStatus(null); setFormErrors(p => ({...p, name: ''})); }}
                        placeholder="Ex : Amadou Diallo"
                        className={`w-full px-4 py-2.5 rounded-xl bg-white/10 border text-white placeholder-slate-500 text-sm focus:outline-none transition-colors ${
                          formErrors.name ? 'border-red-400 bg-red-500/10' : 'border-white/20 focus:border-amber-400'
                        }`}
                      />
                      {formErrors.name && (
                        <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                          <span>⚠</span> {formErrors.name}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 font-semibold uppercase tracking-wide mb-1.5 block">
                        Téléphone <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="tel"
                        value={clientPhone}
                        onChange={e => { const v = e.target.value.replace(/[^0-9+\s]/g, ''); setClientPhone(v); setSent(false); setEmailStatus(null); setFormErrors(p => ({...p, phone: ''})); }}
                        placeholder="Ex : +221 78 000 00 00"
                        className={`w-full px-4 py-2.5 rounded-xl bg-white/10 border text-white placeholder-slate-500 text-sm focus:outline-none transition-colors ${
                          formErrors.phone ? 'border-red-400 bg-red-500/10' : 'border-white/20 focus:border-amber-400'
                        }`}
                      />
                      {formErrors.phone && (
                        <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                          <span>⚠</span> {formErrors.phone}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Boutons d'envoi */}
                  <div className="grid sm:grid-cols-2 gap-3 pt-1">
                    {/* WhatsApp */}
                    <button
                      onClick={handleSendWhatsApp}
                      className="flex items-center justify-center gap-3 bg-green-600 hover:bg-green-500 active:scale-95 text-white px-5 py-3 rounded-xl font-bold text-sm transition-all duration-200"
                    >
                      <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                      </svg>
                      Envoyer par WhatsApp
                    </button>

                    {/* Email */}
                    <button
                      onClick={handleSendEmail}
                      disabled={emailStatus === 'sending'}
                      className={`flex items-center justify-center gap-3 active:scale-95 text-white px-5 py-3 rounded-xl font-bold text-sm transition-all duration-200 ${
                        emailStatus === 'sending'
                          ? 'bg-amber-400 cursor-wait opacity-75'
                          : 'bg-amber-500 hover:bg-amber-400'
                      }`}
                    >
                      {emailStatus === 'sending' ? (
                        <>
                          <svg className="w-4 h-4 animate-spin flex-shrink-0" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                          </svg>
                          Envoi en cours…
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                          </svg>
                          Envoyer par Email
                        </>
                      )}
                    </button>
                  </div>

                  {/* Succès email */}
                  {emailStatus === 'success' && (
                    <div className="flex items-center gap-2 bg-green-500/20 border border-green-500/30 rounded-xl px-4 py-3 mt-2">
                      <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
                      </svg>
                      <p className="text-green-300 text-sm font-semibold">
                        Email envoyé ! Notre équipe vous rappellera dans les plus brefs délais.
                      </p>
                    </div>
                  )}

                  {/* Erreur email */}
                  {emailStatus === 'error' && (
                    <div className="flex items-start gap-2 bg-red-500/20 border border-red-500/30 rounded-xl px-4 py-3 mt-2">
                      <svg className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      <p className="text-red-300 text-sm">{emailError}</p>
                    </div>
                  )}

                  {/* Succès WhatsApp */}
                  {sent && emailStatus !== 'success' && emailStatus !== 'error' && (
                    <div className="flex items-center gap-2 bg-green-500/20 border border-green-500/30 rounded-xl px-4 py-3 mt-2">
                      <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
                      </svg>
                      <p className="text-green-300 text-sm font-semibold">
                        Simulation envoyée sur WhatsApp !
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Disclaimer */}
        <p className="text-center text-xs text-gray-400 mt-5">
          * Simulation indicative. Le montant réel peut varier selon les conditions définitives du contrat.
        </p>
      </div>
    </section>
  );
}

function ResultCard({ icon, label, value, sub, highlight }) {
  return (
    <div
      className={`rounded-2xl p-5 border transition-all duration-200 ${
        highlight
          ? 'bg-amber-50 border-amber-200 shadow-md'
          : 'bg-gray-50 border-gray-100'
      }`}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xl">{icon}</span>
        <span className="text-xs text-gray-500 font-semibold uppercase tracking-wide">{label}</span>
      </div>
      <div className={`text-xl font-bold ${highlight ? 'text-amber-700' : 'text-gray-800'}`}>
        {value}
      </div>
      {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
    </div>
  );
}
